/* ──────────────────────  AdminDashboard.jsx (Compact) ────────────────────── */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Reports from './Reports';
import MenuManager from './MenuManager';
import PendingOrders from './PendingOrders';
import TablesManager from './TablesManager';
import RestaurantProfile from './RestaurantProfile';
import TodayStats from './TodayStats';

import {
  Box, AppBar, Toolbar, Typography, Button, Tabs, Tab, Avatar, Stack
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Dashboard, RestaurantMenu, Assessment, TableBar, Settings, Logout, PendingActions
} from '@mui/icons-material';

// === LUXURY THEME (compact) ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#0f0a00', paper: 'rgba(255,255,255,0.05)' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 24 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '8px 16px',
          fontSize: '0.85rem',
          boxShadow: '0 4px 12px rgba(212,175,55,0.25)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(212,175,55,0.35)',
          },
        },
      },
    },
  },
});

// === COMPACT APPBAR ===
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  boxShadow: '0 4px 12px rgba(212,175,55,0.2)',
  borderBottom: '1px solid rgba(212,175,55,0.3)',
}));

// === GOLD TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

// === COMPACT LUXURY CARD ===
const LuxuryCard = styled('div')(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 24,
  padding: '16px 18px',
  boxShadow: '0 6px 16px rgba(0,0,0,0.07)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 10px 24px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 4,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '24px 24px 0 0',
  },
}));

// === COMPACT TAB ===
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.85rem',
  minWidth: 90,
  color: '#555',
  opacity: 0.9,
  borderRadius: '10px 10px 0 0',
  margin: '0 2px',
  '&.Mui-selected': {
    color: '#1a1a1a',
    fontWeight: 700,
    backgroundColor: 'rgba(255,255,255,0.95)',
    boxShadow: '0 -1px 6px rgba(0,0,0,0.05)',
  },
}));

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) navigate('/admin/login');
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('restaurant_id');
    localStorage.removeItem('restaurant_name');
    navigate('/admin/login');
  };

  const handleTabChange = (e, newVal) => setActiveTab(newVal);

  const tabs = [
    { label: 'Dashboard', icon: <Dashboard /> },
    { label: 'Menu',      icon: <RestaurantMenu /> },
    { label: 'Reports',   icon: <Assessment /> },
    { label: 'Tables',    icon: <TableBar /> },
    { label: 'Profile',   icon: <Settings /> },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ flexGrow: 1, bgcolor: '#f8f8f8', minHeight: '100vh' }}>
        {/* ────── COMPACT HEADER ────── */}
        <StyledAppBar position="sticky">
          <Toolbar sx={{ py: 0.5, minHeight: 56 }}>
            <Avatar sx={{ bgcolor: '#fff', color: '#d4af37', mr: 1.5, width: 36, height: 36 }}>
              <RestaurantMenu fontSize="small" />
            </Avatar>
            <GoldText variant="h6" sx={{ flexGrow: 1, fontSize: '1.1rem' }}>
              {localStorage.getItem('restaurant_name') || 'Admin'}
            </GoldText>
            <Button
              variant="contained"
              startIcon={<Logout />}
              onClick={handleLogout}
              size="small"
              sx={{
                bgcolor: '#c62828',
                '&:hover': { bgcolor: '#b71c1c' },
                px: 2,
                fontSize: '0.8rem',
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </StyledAppBar>

        {/* ────── COMPACT TABS ────── */}
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid rgba(212,175,55,0.3)', px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                bgcolor: '#d4af37',
                height: 3,
                borderRadius: '2px',
              },
            }}
          >
            {tabs.map((t, i) => (
              <StyledTab key={i} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        {/* ────── COMPACT CONTENT ────── */}
        <Box sx={{ p: { xs: 1.5, md: 2 }, height: 'calc(100vh - 100px)' }}>
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
              {/* ────── LEFT: 25% ────── */}
              <Box sx={{ flex: '0 0 25%', minWidth: 240 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  {/* Welcome */}
                  <LuxuryCard>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <GoldText variant="h6" sx={{ fontSize: '1.3rem' }}>
                          Welcome!
                        </GoldText>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Manage efficiently
                        </Typography>
                      </Box>
                      <PendingActions sx={{ fontSize: 40, color: '#d4af37' }} />
                    </Stack>
                  </LuxuryCard>

                  {/* Today Stats */}
                  <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
                    <TodayStats />
                  </Box>
                </Stack>
              </Box>

              {/* ────── RIGHT: 75% ────── */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <LuxuryCard sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ pb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>
                      Pending Orders
                    </Typography>
                  </Box>
                  <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { background: '#f5f5f5', borderRadius: 3 },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#d4af37',
                      borderRadius: 3,
                    },
                  }}>
                    <PendingOrders />
                  </Box>
                </LuxuryCard>
              </Box>
            </Box>
          )}

          {activeTab === 1 && <MenuManager />}
          {activeTab === 2 && <Reports />}
          {activeTab === 3 && <TablesManager />}
          {activeTab === 4 && <RestaurantProfile />}
        </Box>
      </Box>
    </ThemeProvider>
  );
}